import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { useConsultores } from "@/hooks/useConsultores";
import { Search, User } from "lucide-react";

interface ConsultorSearchSelectProps {
  value: string;
  onChange: (consultorId: string, consultorNome: string, consultorCelular: string, consultorCpfCnpj: string, consultorEmail: string) => void;
  label?: string;
}

export function ConsultorSearchSelect({ value, onChange, label = "Selecionar Consultor" }: ConsultorSearchSelectProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [selectedName, setSelectedName] = useState("");

  const { data: consultores, isLoading } = useConsultores();

  useEffect(() => {
    if (value && consultores) {
      const c = consultores.find(i => i.id.toString() === value);
      if (c) {
        setSelectedName(c.nome);
        setSearchTerm(c.nome);
      }
    } else {
      setSelectedName("");
      setSearchTerm("");
    }
  }, [value, consultores]);

  const filtered = consultores?.filter(c => {
    const s = searchTerm.toLowerCase();
    return (
      c.nome.toLowerCase().includes(s) ||
      c.cpf_cnpj?.toLowerCase().includes(s) ||
      c.celular?.toLowerCase().includes(s) ||
      c.email?.toLowerCase().includes(s) ||
      c.id.toString().includes(s)
    );
  }) || [];

  const handleSelect = (c: any) => {
    onChange(
      c.id.toString(),
      c.nome,
      c.celular || "",
      c.cpf_cnpj || "",
      c.email || ""
    );
    setSelectedName(c.nome);
    setSearchTerm(c.nome);
    setIsOpen(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    setIsOpen(newValue.length > 0);
    if (newValue === "") {
      onChange("", "", "", "", "");
      setSelectedName("");
    }
  };

  const handleInputFocus = () => {
    setIsOpen(true);
    if (searchTerm === "" && selectedName !== "") {
      setSearchTerm("");
    }
  };

  const formatCpfCnpj = (cpfCnpj: string) => {
    if (!cpfCnpj) return "";
    if (cpfCnpj.length <= 11) {
      return cpfCnpj.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
    } else {
      return cpfCnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
    }
  };

  return (
    <div className="relative">
      <Label htmlFor="consultor-search">{label}</Label>
      <div className="relative mt-1">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          id="consultor-search"
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          placeholder={isLoading ? "Carregando consultores..." : "Digite ID, nome, CPF/CNPJ, celular ou e-mail..."}
          className="pl-10 w-full"
          disabled={isLoading}
        />
      </div>

      {isOpen && filtered.length > 0 && (
        <Card className="absolute z-50 w-full mt-1 max-h-60 overflow-y-auto border shadow-lg bg-white">
          <div className="p-2">
            {filtered.map((c) => (
              <div
                key={c.id}
                onClick={() => handleSelect(c)}
                className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer rounded-md border-b last:border-b-0"
              >
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <User className="h-4 w-4 text-green-700" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-green-700 bg-green-50 px-2 py-1 rounded border">
                      {c.id}
                    </span>
                    <span className="font-medium text-gray-900">{c.nome}</span>
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    {c.cpf_cnpj && (
                      <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded text-xs">
                        {formatCpfCnpj(c.cpf_cnpj)}
                      </span>
                    )}
                    {c.celular && (
                      <span className="ml-2 text-gray-500">{c.celular}</span>
                    )}
                    {c.email && (
                      <span className="ml-2 text-gray-500">{c.email}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {isOpen && searchTerm && filtered.length === 0 && !isLoading && (
        <Card className="absolute z-50 w-full mt-1 border shadow-lg bg-white">
          <div className="p-4 text-center text-gray-500">Nenhum consultor encontrado</div>
        </Card>
      )}
    </div>
  );
} 